import { EntityRoutes } from "~/api-routes-v2";
import { parseContentDisposition } from "~/features/entity-export/parseContentDisposition";
import type { ExportRequestPayload } from "~/features/entity-export/types";

const FALLBACK_FILENAME = "Entity Data Export.xlsx";

export interface EntityExportResult {
  blob: Blob;
  filename: string;
}

/**
 * Calls the entity export API and returns the Excel blob plus filename from
 * Content-Disposition. Uses fetch directly because the response is a blob
 * and we need to read response headers; apiService does not support this.
 *
 * @param payload - Export request body (entityIds + datasets)
 * @param token - Bearer token (e.g. from useAuth().getAccessToken()). If null, request is sent without Authorization (will likely 401).
 */
export async function requestEntityExport(
  payload: ExportRequestPayload,
  token: string | null,
): Promise<EntityExportResult> {
  const url = EntityRoutes.exportEntities;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = response.statusText || "Export failed";
    try {
      const json = await response.json();
      if (typeof (json as { message?: string }).message === "string") {
        message = (json as { message: string }).message;
      }
    } catch {
      // Response was not JSON; use statusText
    }
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("Content-Disposition");
  const filename =
    parseContentDisposition(contentDisposition) ?? FALLBACK_FILENAME;

  return { blob, filename };
}
